import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { sideBarList, helpSupportList, adminSidebarList, mentorSidebarList, subMenus, sidebarCategories } from "../Sidebar/SidebarData";

export const useBreadcrumbs = () => {
  const location = useLocation();
  
  return useMemo(() => {
    if (location.pathname === '/dashboard/home') return [];
    
    const breadcrumbs = [{ name: 'Dashboard', path: '/dashboard/home', icon: 'home' }];
    const categoryItems = sidebarCategories.flatMap(category => category.items);
    const allItems = [...categoryItems, ...helpSupportList]; // Include help support items
    
    // Generic breadcrumb logic that auto-detects parent routes
    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    const relevantSegments = pathSegments.filter(segment => segment !== 'dashboard');
    
    if (relevantSegments.length > 0) {
      // Special handling for upskilling pathway routes - check first
      if (location.pathname === '/dashboard/skills-and-learning/my-upskilling-pathway') {
        breadcrumbs.push({ name: 'Skills and Learning', path: '/dashboard/skills-and-learning', isLast: false });
        breadcrumbs.push({ name: 'My Upskilling Pathway', path: location.pathname, isLast: true });
      } else if (location.pathname === '/dashboard/skills-and-learning/upskilling-pathway-config') {
        breadcrumbs.push({ name: 'Skills and Learning', path: '/dashboard/skills-and-learning', isLast: false });
        breadcrumbs.push({ name: 'Upskilling Pathway Config', path: location.pathname, isLast: true });
      } else if (location.pathname === '/dashboard/chats-with-mentees') {
        breadcrumbs.push({ name: 'My Mentees', path: location.pathname, isLast: true });
      } else {
        // Check if we have a direct match first in all items (category + help support)
        // Handle query parameters by comparing pathname without query params
        let matchingItem = allItems.find(item => {
          const itemPath = item.link.split('?')[0]; // Remove query parameters
          return itemPath === location.pathname;
        });
        
        if (matchingItem) {
          // Direct match found - check if it has a parent route
          const pathParts = location.pathname.split('/');
          if (pathParts.length > 3) { // More than /dashboard/parent/child
            // Check if this is a Help & Support item
            if (helpSupportList.some(item => item.link === location.pathname)) {
              breadcrumbs.push({ name: 'Help & Support', path: '/dashboard/help-and-support', isLast: false });
            } else {
              // Find which category contains this item
              const parentCategory = sidebarCategories.find(category => 
                category.items.some(item => {
                  const itemPath = item.link.split('?')[0]; // Remove query parameters
                  return itemPath === location.pathname;
                })
              );
              if (parentCategory) {
                const parentPath = pathParts.slice(0, -1).join('/'); // Remove last segment
                breadcrumbs.push({ name: parentCategory.name, path: parentPath, isLast: false });
              }
            }
          }
          breadcrumbs.push({ name: matchingItem.name, path: matchingItem.link, isLast: true });
        } else {
          // No direct match - try to find parent and child
          const parentSegment = relevantSegments[0];
          const parentPath = `/dashboard/${parentSegment}`;
          
          // Check if this is a Help & Support route
          if (helpSupportList.some(item => item.link === location.pathname)) {
            breadcrumbs.push({ name: 'Help & Support', path: '/dashboard/help-and-support', isLast: false });
            const childItem = helpSupportList.find(item => item.link === location.pathname);
            if (childItem) {
              breadcrumbs.push({ name: childItem.name, path: childItem.link, isLast: true });
            }
          } else {
            // Look for parent in sidebar categories by finding which category contains an item with the current path
            const parentCategory = sidebarCategories.find(category => 
              category.items.some(item => {
                const itemPath = item.link.split('?')[0]; // Remove query parameters
                return itemPath === location.pathname;
              })
            );
            
            if (parentCategory) {
              breadcrumbs.push({ name: parentCategory.name, path: parentPath, isLast: false });
              
              // Find the specific child item
              const childItem = parentCategory.items.find(item => {
                const itemPath = item.link.split('?')[0]; // Remove query parameters
                return itemPath === location.pathname;
              });
              if (childItem) {
                breadcrumbs.push({ name: childItem.name, path: childItem.link, isLast: true });
              }
            } else {
              // Fallback to generic parent detection
              const parentItem = categoryItems.find(item => 
                item.link.includes(parentSegment) || 
                item.name.toLowerCase().includes(parentSegment.replace('-', ' '))
              );
              
              if (parentItem) {
                breadcrumbs.push({ name: parentItem.name, path: parentItem.link, isLast: false });
                
                const submenuItems = subMenus[parentItem.name];
                if (submenuItems) {
                  const submenuItem = submenuItems.find(item => item.link === location.pathname);
                  if (submenuItem) {
                    breadcrumbs.push({ name: submenuItem.name, path: submenuItem.link, isLast: true });
                  }
                }
              }
            }
          }
        }
      }
    }
    
    return breadcrumbs;
  }, [location.pathname]);
};